import type {ReactNode} from 'react'
import {useState} from 'react'
import {NewToolHeader} from './tool/NewToolHeader'
import {AnimatedCollapse} from '../AnimatedCollapse'
import styles from './ResolvedPromptDisplay.module.css'

export interface ResolvedPromptDisplayProps {
  /** Leading icon for the header (e.g. QuestionIcon, ShieldCheckIcon) */
  icon?: ReactNode
  /** Title text shown in the header */
  title: string
  /** Optional subtitle shown after the title */
  subtitle?: string
  /** Expandable content shown below the header when toggled (inside muted box) */
  children?: ReactNode
  /** Optional footer shown below the collapsible area */
  footer?: ReactNode
  /** Whether to start collapsed when children are present (default: false, matching legacy behavior) */
  defaultCollapsed?: boolean
}

/** Renders a resolved interactive prompt in the same style as Tool calls */
export function ResolvedPromptDisplay({
  icon,
  title,
  subtitle,
  children,
  footer,
  defaultCollapsed = false,
}: ResolvedPromptDisplayProps) {
  const hasExpandable = children != null
  const [isCollapsed, setIsCollapsed] = useState(hasExpandable ? defaultCollapsed : true)

  const displayTitle = subtitle ? `${title} · ${subtitle}` : title

  return (
    <div>
      <NewToolHeader
        leadingVisual={icon}
        title={displayTitle}
        isCollapsed={isCollapsed}
        hasReferences={hasExpandable}
        onToggle={() => setIsCollapsed(prev => !prev)}
      />
      {hasExpandable && (
        <AnimatedCollapse open={!isCollapsed}>
          <div className={styles.content}>{children}</div>
        </AnimatedCollapse>
      )}
      {footer}
    </div>
  )
}
